/*
 * UP OR DOWN, AND HOW MUCH (2026-10-19). Chris, on the metrics bar: "arrows going up or down ... The arrows are 3 max either up or down and have a faded look as they go up or down" (and later: the brightest at the top for an up stack, at the bottom for a down one), and the colour is
 * NEUTRAL (Skydive shows behaviour and leaves the judgement to the person: bounce rate going down is not good or bad).
 *
 * How big a change earns how many arrows is ONE table (ARROW_STEPS), and when no arrow is shown at all is two rules, both about not drawing a confident picture of noise:
 *   1. not enough sessions: under MIN_SESSIONS ended sessions in either interval, "up 300%" from three visits to twelve is chance, so nothing is shown;
 *   2. a change that chance could produce: a COUNT (page views, sessions, conversions) is tested as a Poisson count, a RATE (bounce rate, % new, conversion rate) as two proportions, and an arrow is
 *      shown only when the difference is beyond what chance usually makes (|z| of at least 1.96, about 95%). An AVERAGE (pages per session, session length) has no spread to test with here, so only
 *      rule 1 and the size of the change apply to it.
 * The numbers below are the first proposal, not measured: change them HERE and nowhere else.
 *
 * A number that came out flat, and a number that could not be compared at all ("unknown": too few sessions, or no numbers), are both drawn as NOTHING. (A dash for "flat" was built and removed,
 * Chris, 2026-10-19: "It looks like a literal minus sign".) The hover card tells them apart: "About the same as ..." / "No clear change from ..." against no comparison sentence at all.
 */
export const MIN_SESSIONS = 30;
/** the relative change at which a trend earns its 1st, 2nd and 3rd arrow */
export const ARROW_STEPS: readonly [number, number, number] = [0.02, 0.1, 0.25];
export const MAX_ARROWS = 3;
export const Z_CRITICAL = 1.96;

export type MetricKind = "count" | "rate" | "average";
export type TrendDirection = "up" | "down" | "flat" | "unknown";

export interface TrendInput {
  kind: MetricKind;
  current: number;
  previous: number;
  /** ended sessions in each interval: the sample the numbers rest on */
  currentSessions: number;
  previousSessions: number;
}

export interface Trend {
  direction: TrendDirection;
  /** 0 to 3 */
  arrows: number;
  /** relative change: 0.152 is up 15.2%; Infinity when it grew from nothing; NaN when unknown */
  change: number;
  /** why there is nothing to show, when direction is "unknown" or "flat" */
  reason?: "no sample" | "too small" | "within chance";
}

const unknown = (reason: Trend["reason"]): Trend => ({ direction: "unknown", arrows: 0, change: NaN, reason });

/** the number of arrows for a relative change */
export const arrowsFor = (change: number): number => {
  const size = Math.abs(change);
  if (!(size >= ARROW_STEPS[0])) return 0;

  return size >= ARROW_STEPS[2] ? 3 : size >= ARROW_STEPS[1] ? 2 : 1;
};

/** |z| for two counts taken over the same exposure (equal intervals): (a - b) / sqrt(a + b) */
const zForCounts = (a: number, b: number): number => (a + b > 0 ? (a - b) / Math.sqrt(a + b) : 0);

/** |z| for two proportions (as percentages) over their own sample sizes, with the pooled proportion */
const zForRates = (pctA: number, nA: number, pctB: number, nB: number): number => {
  const pA = pctA / 100;
  const pB = pctB / 100;
  const pooled = (pA * nA + pB * nB) / (nA + nB);
  const spread = Math.sqrt(pooled * (1 - pooled) * (1 / nA + 1 / nB));

  return spread > 0 ? (pA - pB) / spread : 0;
};

export const trendOf = (input: TrendInput): Trend => {
  const { kind, current, previous, currentSessions, previousSessions } = input;
  if (![current, previous, currentSessions, previousSessions].every((n) => Number.isFinite(n))) return unknown("no sample");
  if (currentSessions < MIN_SESSIONS || previousSessions < MIN_SESSIONS) return unknown("too small");
  if (current === previous) return { direction: "flat", arrows: 0, change: 0 };

  const change = previous === 0 ? (current > 0 ? Infinity : -Infinity) : (current - previous) / Math.abs(previous);
  const z = kind === "count" ? zForCounts(current, previous) : kind === "rate" ? zForRates(current, currentSessions, previous, previousSessions) : Infinity;
  if (Math.abs(z) < Z_CRITICAL) return { direction: "flat", arrows: 0, change, reason: "within chance" };
  const arrows = arrowsFor(change);
  if (arrows === 0) return { direction: "flat", arrows: 0, change };

  return { direction: change > 0 ? "up" : "down", arrows, change };
};

/** "15.2%" for a change of 0.152 (whole numbers from 100%: "120%"); nothing for a change that has no size (it grew from nothing), which trendSentence says in words */
export const changeText = (change: number): string => {
  if (!Number.isFinite(change)) return "";
  const pct = Math.abs(change) * 100;

  return `${pct >= 100 ? Math.round(pct).toLocaleString("en-US") : pct.toFixed(1)}%`;
};

/** The sentence the hover card adds under a metric's definition, or "" when there is nothing honest to say. */
export const trendSentence = (trend: Trend, previousValueText: string, previousLabelText: string): string => {
  if (trend.direction === "unknown") return trend.reason === "too small" ? `Not enough sessions in one of the two periods to compare with ${previousLabelText}.` : "";
  // flat: either the change is under the size that earns an arrow ("about the same"), or it is bigger than that but no bigger than chance makes ("no clear change": a rate that fell 14% on a small
  // sample is not "about the same", and it is not a fall either)
  if (trend.direction === "flat") return trend.reason === "within chance" ? `No clear change from ${previousLabelText} (${previousValueText}).` : `About the same as ${previousLabelText} (${previousValueText}).`;
  if (!Number.isFinite(trend.change)) return `${trend.direction === "up" ? "Up" : "Down"} from none in ${previousLabelText}.`;

  return `${trend.direction === "up" ? "Up" : "Down"} ${changeText(trend.change)} from ${previousLabelText} (${previousValueText}).`;
};

/** The opacity of each arrow in a stack of `arrows`, from the one at the TIP (furthest the way the number moved) backwards: the tip is brightest, and each arrow behind it is fainter. */
export const arrowOpacities = (arrows: number): number[] => Array.from({ length: Math.min(MAX_ARROWS, Math.max(0, arrows)) }, (_, i) => [1, 0.6, 0.3][i]);
